import { useCallback, useEffect, useRef, useState } from 'react';
import { extractErrorMessage } from '@kardume/services';

/** Minimal async-data hook with loading/error state and manual reload. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Última chamada disparada. Só ela pode escrever no estado.
   *
   * Sem isso, trocar de filtro ou de página rápido deixava o resultado da requisição
   * antiga - que voltou depois - na tela, com os controles mostrando outra coisa.
   */
  const ticket = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(() => {
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    return fn()
      .then((result) => {
        if (mine === ticket.current) setData(result);
      })
      .catch((err) => {
        if (mine === ticket.current) {
          setError(extractErrorMessage(err, 'Não foi possível carregar os dados.'));
        }
      })
      .finally(() => {
        if (mine === ticket.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
  }, [run]);

  return { data, loading, error, reload: run, setData };
}
